import { z } from 'zod';

/**
 * Strict, RFC-pragmatic email regex shared by every email field in the app.
 * Rejects garbage like `asdf@!asd.com`, `foo@bar`, `foo@.com` that zod's
 * default `.email()` would accept.
 */
export const STRICT_EMAIL_RE =
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/;

export const isValidEmail = (value: string): boolean =>
  STRICT_EMAIL_RE.test(value.trim());

/** Reusable strict email zod schema. Trims, length-caps, and format-checks. */
export const strictEmail = (message = 'Please enter a valid email address') =>
  z.string().trim().max(255, 'Email must be less than 255 characters').regex(STRICT_EMAIL_RE, message);

// Login validation schema
export const loginSchema = z.object({
  email: strictEmail(),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().default(false),
});

export type LoginFormData = z.infer<typeof loginSchema>;

// Sign up validation schema
/** One password rule for every sign-up and reset form. */
export const strongPassword = z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number');

export const signUpSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(100),
  lastName: z.string().trim().min(1, 'Last name is required').max(100),
  podcastShowName: z.string().trim().max(200).optional().or(z.literal('')),
  email: strictEmail(),
  phone: z.string().trim().min(1, 'Phone number is required').max(20),
  password: strongPassword,
  confirmPassword: z.string(),
  termsAccepted: z.literal(true, {
    errorMap: () => ({ message: 'You must accept the Terms of Service and Privacy Policy' }),
  }),
  smsOptIn: z.boolean().default(false),
  hasExistingRss: z.boolean().default(false),
  existingRssUrl: z.string().trim().max(2000).optional().or(z.literal('')),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
}).refine((data) => !data.hasExistingRss || (data.existingRssUrl && data.existingRssUrl.length > 0), {
  message: 'RSS feed URL is required when importing an existing podcast',
  path: ['existingRssUrl'],
}).refine((data) => {
  if (!data.hasExistingRss || !data.existingRssUrl) return true;
  try { new URL(data.existingRssUrl); return true; } catch { return false; }
}, {
  message: 'Please enter a valid URL',
  path: ['existingRssUrl'],
});

export type SignUpFormData = z.infer<typeof signUpSchema>;

// Forgot password validation schema
export const forgotPasswordSchema = z.object({
  email: strictEmail(),
});

export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

// Reset password validation schema
export const resetPasswordSchema = z.object({
  password: strongPassword,
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

// Team invite validation schema
export const teamInviteSchema = z.object({
  email: strictEmail(),
});

export type TeamInviteFormData = z.infer<typeof teamInviteSchema>;

// Waitlist (guest) validation schema
export const waitlistSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(100),
  lastName: z.string().trim().min(1, 'Last name is required').max(100),
  email: strictEmail(),
  phone: z.string().trim().max(20).optional().or(z.literal('')),
});

export type WaitlistFormData = z.infer<typeof waitlistSchema>;

// Job application validation schema
export const jobApplicationSchema = z.object({
  first_name: z.string().trim().min(1, 'First name is required').max(100),
  last_name: z.string().trim().min(1, 'Last name is required').max(100),
  email: strictEmail(),
  phone: z.string().trim().max(20).optional().or(z.literal('')),
  cover_message: z.string().trim().max(2000).optional().or(z.literal('')),
});

export type JobApplicationFormData = z.infer<typeof jobApplicationSchema>;

// YouTube URL validation schema
const YOUTUBE_URL_RE =
  /^https?:\/\/(?:www\.)?(?:youtube\.com\/(?:watch\?.*v=|shorts\/)|youtu\.be\/|m\.youtube\.com\/watch\?.*v=)[\w-]{11}/;

export const youtubeUrlSchema = z
  .string()
  .trim()
  .min(1, 'YouTube URL is required')
  .refine((val) => YOUTUBE_URL_RE.test(val), {
    message: 'Please enter a valid YouTube URL (youtube.com/watch, youtu.be, or youtube.com/shorts)',
  });

